import { ApolloError } from '@apollo/client';
import { useMemo } from 'react';

import * as GQL from '~/graphql/generated/client';
import { normalizeArray } from '~/shared/utils/user-journey-ranking';
import {
  buildAnswerMap,
  evaluateAllVisibility,
  Dependency,
} from '~/shared/utils/visibility-engine';

export interface UseAllControlVisibilityParams {
  policyIdentifier: string;
  orgId?: string;
  policyVersion?: string;
  role?: string;
  controlBundles: GQL.ControlBundleFragment[];
  answerBundles: GQL.AnswerBundleFragment[];
  skip?: boolean;
  /** Project-level screening visibility map — controls with visible:false are excluded */
  screeningVisibility?: Record<
    string,
    { visible: boolean; reason?: string; ruleId?: string }
  > | null;
}

export interface UseAllControlVisibilityResult {
  /** Map from controlBundle._id → Set of visible control identifiers. Empty map = all visible. */
  bundleVisibility: Map<string, Set<string>>;
  loading: boolean;
  error?: ApolloError;
  isConditionalLogicEnabled: boolean;
}

/**
 * Hook to calculate control visibility for ALL bundles in an assessment.
 *
 * Uses the shared visibility engine (topological sort O(n+m)) to evaluate
 * which controls should be visible based on current answers.
 *
 * Uses `cache-first` fetchPolicy so that if `useControlVisibility` (which uses
 * `network-only`) already fetched the UserJourney, this hook returns instantly
 * from Apollo cache with zero network calls.
 *
 * Implements fail-open strategy: returns empty Map (= all controls visible) if:
 * - UserJourney config not found
 * - Query errors occur
 * - Bundle has no dependencies configured
 */
export function useAllControlVisibility(
  params: UseAllControlVisibilityParams
): UseAllControlVisibilityResult {
  const {
    policyIdentifier,
    orgId,
    policyVersion,
    role,
    controlBundles,
    answerBundles,
    skip = false,
    screeningVisibility,
  } = params;

  const shouldSkip = skip || !policyIdentifier;

  const { data, loading, error } = GQL.useUserJourneyByPolicyIdentifierQuery({
    variables: { policyIdentifier, orgId, policyVersion, role },
    skip: shouldSkip,
    fetchPolicy: 'cache-first',
  });

  const { bundleVisibility, isConditionalLogicEnabled } = useMemo(() => {
    const emptyResult = {
      bundleVisibility: new Map<string, Set<string>>(),
      isConditionalLogicEnabled: false,
    };

    if (
      loading ||
      !data?.userJourneyByPolicyIdentifier ||
      controlBundles.length === 0
    ) {
      return emptyResult;
    }

    const ujBundles = data.userJourneyByPolicyIdentifier.bundles;
    const resultMap = new Map<string, Set<string>>();
    let hasAnyDeps = false;

    for (const cb of controlBundles) {
      // Apply screening pre-filter before UserJourney evaluation
      const screeningFilteredControlIds = screeningVisibility
        ? cb.controls
            .filter((c) => {
              const entry = screeningVisibility[c.identifier];
              return !entry || entry.visible !== false;
            })
            .map((c) => c.identifier)
        : null;

      // Find matching BundleConfig (identifier + version)
      let bundleConfig: GQL.BundleConfig | null = null;
      try {
        bundleConfig =
          ujBundles?.find((b) => {
            if (b.controlBundleIdentifier !== cb.identifier) {
              return false;
            }
            const versions = normalizeArray(b.controlBundleVersions);
            return (
              (cb.version && versions.includes(cb.version)) ||
              versions.includes('*')
            );
          }) ?? null;
      } catch (e) {
        console.error('Invalid UserJourney data structure', e);
        continue;
      }

      // No config or no dependencies → apply only screening filter if any
      const dependencies = bundleConfig?.dependencies;
      if (!dependencies || dependencies.length === 0) {
        // If screening hides some controls, still record the filtered set
        if (screeningFilteredControlIds) {
          const allIds = cb.controls.map((c) => c.identifier);
          if (screeningFilteredControlIds.length !== allIds.length) {
            hasAnyDeps = true;
            resultMap.set(cb._id, new Set(screeningFilteredControlIds));
          }
        }
        continue;
      }

      // Find matching answer bundle
      const answerBundle = answerBundles.find(
        (ab) => ab.controlBundleId === cb._id
      );

      // No answers yet → apply only screening filter if any
      if (!answerBundle) {
        if (screeningFilteredControlIds) {
          const allIds = cb.controls.map((c) => c.identifier);
          if (screeningFilteredControlIds.length !== allIds.length) {
            hasAnyDeps = true;
            resultMap.set(cb._id, new Set(screeningFilteredControlIds));
          }
        }
        continue;
      }

      hasAnyDeps = true;

      const controlIds =
        screeningFilteredControlIds ?? cb.controls.map((c) => c.identifier);
      const answerMap = buildAnswerMap(answerBundle.answers);
      const visibilityMap = evaluateAllVisibility(
        dependencies as Dependency[],
        answerMap,
        controlIds
      );

      const visibleSet = new Set<string>();
      visibilityMap.forEach((isVisible, id) => {
        if (isVisible) {
          visibleSet.add(id);
        }
      });

      resultMap.set(cb._id, visibleSet);
    }

    return {
      bundleVisibility: resultMap,
      isConditionalLogicEnabled: hasAnyDeps,
    };
  }, [data, controlBundles, answerBundles, loading, screeningVisibility]);

  // Handle error at return level (same pattern as useControlVisibility)
  if (error) {
    return {
      bundleVisibility: new Map(),
      loading: false,
      error,
      isConditionalLogicEnabled: false,
    };
  }

  return {
    bundleVisibility,
    loading,
    error,
    isConditionalLogicEnabled,
  };
}

/**
 * Filter assessment data to exclude hidden controls based on visibility rules.
 *
 * Returns the original reference unchanged if:
 * - bundleVisibility is empty (no rules = all visible)
 * - No controls were actually filtered out
 */
export function filterAssessmentByVisibility(
  assessmentData: GQL.AssessmentFragment,
  bundleVisibility: Map<string, Set<string>>
): GQL.AssessmentFragment {
  if (bundleVisibility.size === 0) {
    return assessmentData;
  }

  let changed = false;

  const filteredBundles = assessmentData.policy.controlBundleList.map((cb) => {
    const visibleIds = bundleVisibility.get(cb._id);
    if (!visibleIds) {
      return cb;
    }
    const filtered = cb.controls.filter((c) => visibleIds.has(c.identifier));
    if (filtered.length !== cb.controls.length) {
      changed = true;
    }
    return { ...cb, controls: filtered };
  });

  const filteredAnswerBundles = assessmentData.answerBundles.map((ab) => {
    const visibleIds = bundleVisibility.get(ab.controlBundleId);
    if (!visibleIds) {
      return ab;
    }
    const filtered = ab.answers.filter((a) =>
      visibleIds.has(a.controlIdentifier)
    );
    if (filtered.length !== ab.answers.length) {
      changed = true;
    }
    return { ...ab, answers: filtered };
  });

  if (!changed) {
    return assessmentData;
  }

  return {
    ...assessmentData,
    policy: {
      ...assessmentData.policy,
      controlBundleList: filteredBundles,
    },
    answerBundles: filteredAnswerBundles,
  };
}
